import { useQuery } from '@apollo/client';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { SelectValue } from 'elements/minute/sidebar/sidebarComponents/atomic/SelectValue';
import { GuideQuery, GuideQueryVariables } from 'graphql/query/__generated__/GuideQuery';
import { GuidesForCurrentUserQuery } from 'graphql/query/__generated__/GuidesForCurrentUserQuery';
import { CURRENT_USER_GUIDES_QUERY, GUIDE_QUERY } from 'graphql/query/guideQuery';
import {
    IMPORT_DETAIL_PARSERS,
    ImportOperationsResult,
    prepareImportPayloadData
} from 'pages/special/ImportDetailsPage/prepareImportPayloadData';
import { useContext, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { GlobalContext } from 'session/state/context';
import styled from 'styled-components/macro';

interface Props {
    setFields: (fields: string[]) => void;
    setImportData: (data: ImportOperationsResult) => void;
    setResponses: (responses: string[]) => void;
    setReuploadImages: (reuploadImages: boolean) => void;
    setAssignToListId: (listId: string) => void;
    assignListId: string;
    setGuideId: (guideId: string) => void;
    className?: string;
}

export const ParserConfig = (props: Props): JSX.Element => {
    const globalCtx = useContext(GlobalContext);
    const allGuides = useQuery<GuidesForCurrentUserQuery>(CURRENT_USER_GUIDES_QUERY);
    const allParsers = Object.keys(IMPORT_DETAIL_PARSERS);

    const [textValue, setTextValue] = useState('');
    const [guideId, setGuideId] = useState<string>('');
    const guideQuery = useQuery<GuideQuery, GuideQueryVariables>(GUIDE_QUERY, {
        variables: { guideId }
    });
    const [parserName, setParserName] = useState<string>(null);
    const { readString } = usePapaParse();

    const handleReadCSV = () => {
        readString(textValue?.trim(), {
            worker: false,
            header: true,
            transformHeader: function (header) {
                return header?.trim();
            },
            transform: function (value) {
                return value?.trim();
            },
            complete: async (results) => {
                if (!guideQuery.data?.guide) {
                    alert('Guide not found');
                }

                console.log('---------------------------');
                console.log(results);
                console.log('---------------------------');
                if (results.errors.length) {
                    console.log('results.erorrs', results.errors);
                    alert('Error parsing file, see more in console.log');
                    return;
                }
                const fields = results.meta.fields;
                const rows = results.data;

                props.setFields(fields);

                const preparedData = prepareImportPayloadData(
                    parserName,
                    guideQuery.data.guide,
                    rows,
                    props.assignListId
                );
                props.setImportData(preparedData);
            }
        });
    };

    return (
        <S_ParserConfig className={props.className}>
            <S_Label>
                <S_LabelText>Guide:</S_LabelText>
                <S_SelectValue
                    options={allGuides.data?.currentUserGuides?.map((e) => ({
                        label: e.title,
                        value: e.guideId
                    }))}
                    onChange={(e) => {
                        setGuideId(e.value);
                        props.setGuideId(e.value);
                    }}
                />
            </S_Label>
            <S_Label>
                <S_LabelText>Guide ID:</S_LabelText>
                {guideId}
            </S_Label>
            <S_Label>
                <S_LabelText>User ID:</S_LabelText>
                {globalCtx.state.loggedUser?.userId}
            </S_Label>
            <S_Label>
                <S_LabelText>User Email:</S_LabelText>
                {globalCtx.state.loggedUser?.email}
            </S_Label>
            <S_Label>
                <S_LabelText>Parser Name:</S_LabelText>
                <S_SelectValue
                    options={allParsers.map((e) => ({ label: e, value: e }))}
                    onChange={(e) => setParserName(e.value)}
                />
            </S_Label>
            <S_Label>
                <S_LabelText>Reupload Images</S_LabelText>
                <S_SelectValue
                    options={[
                        { label: 'Yes', value: '1' },
                        { label: 'No', value: '0' }
                    ]}
                    onChange={(e) => props.setReuploadImages(e.value === '1')}
                />
            </S_Label>
            <S_Label>
                <S_LabelText>Assign to list</S_LabelText>
                <S_SelectValue
                    options={[
                        { label: '-', value: '0' },
                        ...(guideQuery.data?.guide?.entityTypes.map((e) => ({
                            label: e.name,
                            value: e.guideEntityTypeId
                        })) ?? [])
                    ]}
                    selectedOption={
                        props.assignListId
                            ? {
                                  label: guideQuery.data?.guide?.entityTypes.find(
                                      (e) => e.guideEntityTypeId === props.assignListId
                                  )?.name,
                                  value: props.assignListId
                              }
                            : { label: '-', value: '0' }
                    }
                    onChange={(e) =>
                        e.value === '0'
                            ? props.setAssignToListId(null)
                            : props.setAssignToListId(e.value)
                    }
                />
                <S_NoteText>(this options unassigns not imported items from the list)</S_NoteText>
            </S_Label>

            <S_TextArea
                placeholder="CSV File here"
                rows={10}
                value={textValue}
                onChange={(e) => setTextValue(e.target.value)}
            />
            <S_Row>
                <ActionButton
                    text="Clear"
                    onClick={() => {
                        props.setFields(null);
                        props.setImportData(null);
                        props.setResponses([]);
                    }}
                />
                <ActionButton text="Parse" onClick={handleReadCSV} />
            </S_Row>
        </S_ParserConfig>
    );
};

const S_ParserConfig = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const S_Row = styled.div`
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    gap: 10px;
`;

const S_Label = styled.label`
    display: flex;
    flex-direction: row;
    gap: 50px;
    margin-bottom: 20px;
`;

const S_LabelText = styled.div`
    width: 100px;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const S_NoteText = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const S_SelectValue = styled(SelectValue)`
    width: 200px;
`;

const S_TextArea = styled.textarea`
    padding: 10px;
    border: 1px solid #ccc;
`;
